/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { ref } from 'ember-ref-bucket';
import { defaultErrorHandler } from 'volta/utils/error-utils';

import type { ComponentLike, ContentValue } from '@glint/template';
import type { Dropdown } from 'ember-basic-dropdown/components/basic-dropdown';
import type { CalculatePositionResult } from 'ember-basic-dropdown/utils/calculate-position';
import type { TError } from 'volta/utils/error-utils';
import type { TText } from 'volta/utils/text-utils';
import type { BvFocusUnitModifier } from '../bv-focus-zone';
import type { BvPopoverApi, BvPopoverSignature } from '../bv-popover';
import type { TTooltipSide } from '../bv-tooltip';

export interface BvInlineEditApi {
  isEditing: boolean;
  isLoading: boolean;
  popover: BvPopoverApi;
  toggle: Dropdown['actions']['toggle'];
  close: Dropdown['actions']['close'];
  confirm: (e: Event) => Promise<void> | void;
}

type PopoverArgs = Pick<
  BvPopoverSignature['Args'],
  | 'disabled'
  | 'horizontalPosition'
  | 'isMenu'
  | 'matchTriggerWidth'
  | 'onOpen'
  | 'preventScroll'
  | 'renderInPlace'
  | 'tabindex'
  | 'verticalPosition'
  | 'size'
>;

export type BvInlineEditArgs = PopoverArgs & {
  tooltipSide?: TTooltipSide;
  tooltipText?: TText;
  flushBody?: boolean;
  withDisplay?: boolean;
  title?: ContentValue | ComponentLike;
  hideDefaultActions?: boolean;
  fullWidth?: boolean;
  stopPropagation?: boolean;
  focusZoneUnit?: BvFocusUnitModifier;
  enableFocusTrap?: boolean;
  testName?: string;
  displayClassNames?: string;
  onSave?: (e: Event) => void | Promise<void>;
  onCancel?: () => void;
};

export type BvInlineEditCommonArgs = Pick<
  BvInlineEditArgs,
  | 'hideDefaultActions'
  | 'matchTriggerWidth'
  | 'disabled'
  | 'tooltipText'
  | 'testName'
  | 'displayClassNames'
>;

interface BvInlineEditSignature {
  Args: BvInlineEditArgs;
  Blocks: {
    actions: [BvInlineEditApi];
    default: [BvInlineEditApi];
    editing: [BvInlineEditApi];
  };
  Element: HTMLElement;
}

export default class BvInlineEditComponent extends Component<BvInlineEditSignature> {
  @tracked isLoading: boolean = false;
  @tracked popover!: Dropdown;

  @ref('displayElement')
  displayElement: HTMLInputElement | null = null;

  @action
  handlePopoverInit(api: Dropdown) {
    this.popover = api;
  }

  @action
  handlePopoverClose(_api: Dropdown, event?: Event) {
    if ((event?.target as HTMLElement) === document.body) {
      return false;
    }

    if (!this.args.disabled) {
      return this.args.onCancel?.();
    }
  }

  @action
  handleEscape(event: KeyboardEvent) {
    event.stopImmediatePropagation();
    this.popover?.actions.close(event, false);
  }

  @action
  setPositionCorrectly(pos: CalculatePositionResult): CalculatePositionResult {
    const padding = 5;
    const offset =
      ((this.displayElement?.offsetHeight ?? 0) + padding) *
      (pos.verticalPosition == 'above' ? 1 : -1);

    pos.style.top = pos.style.top ?? offset;

    return pos;
  }

  @action
  async handleConfirmation(event: Event) {
    const { onSave } = this.args;

    if (!this.args.disabled && onSave) {
      this.isLoading = true;
      try {
        await onSave(event);
        this.popover?.actions.close(event, false);
      } catch (error) {
        defaultErrorHandler(error as TError);
      } finally {
        this.isLoading = false;
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvInlineEdit: typeof BvInlineEditComponent;
    'bv-inline-edit': typeof BvInlineEditComponent;
  }
}
